'use client'

import { InfoIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import {
  AUTO_STEPS,
  MOTORCYCLE_STEPS,
  Step,
} from '@/blocks/FlchPathToLicense/steps'
import { PageInfo } from '@/src/@types/models'
import Button from '@/src/components/core/Button'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/src/components/core/Tabs'
import Container from '@/src/components/layout/Container'

export default function Component({ pageInfo }: { pageInfo: PageInfo }) {
  return (
    <div
      className={clsx({
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container>
        <Tabs defaultValue="auto">
          <div className="mx-auto flex flex-col items-center gap-8 md:max-w-208">
            <div className="flex flex-col gap-4 text-center md:gap-6">
              <h2 className="text-4xl font-black md:text-20xl">
                Schritt für Schritt zum Führerschein
              </h2>
              <p className="text-gray-800 md:text-lg">
                Alle Schritte zum Führerschein im Überblick. Von der Anmeldung
                bis zur Prüfung - hier sind sämtliche Etappen übersichtlich
                zusammengefasst.
              </p>
            </div>
            <TabsList tabCount={2} className="w-full bg-gray-150 md:w-auto">
              <TabsTrigger value="auto">Kat. B (Auto)</TabsTrigger>
              <TabsTrigger value="motorrad">Kat. A (Motorrad)</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="auto" className="mt-12 md:mt-24">
            <Timeline steps={AUTO_STEPS} />
          </TabsContent>
          <TabsContent value="motorrad" className="mt-12 md:mt-24">
            <Timeline steps={MOTORCYCLE_STEPS} />
          </TabsContent>
        </Tabs>
      </Container>
    </div>
  )
}

function Timeline({ steps }: { steps: Step[] }) {
  return (
    <div>
      {steps.map((step, index) => (
        <TimelineStep
          key={step.step}
          step={step}
          isFirst={index === 0}
          isLast={index === steps.length - 1}
          alignEnd={index % 2 === 1}
        />
      ))}
    </div>
  )
}

function TimelineStep({
  step,
  isFirst,
  isLast,
  alignEnd,
}: {
  step: Step
  isFirst: boolean
  isLast: boolean
  alignEnd: boolean
}) {
  return (
    <div className="flex gap-3">
      <div
        className={clsx(
          'hidden flex-1 md:block',
          alignEnd ? 'md:order-1' : 'md:order-3'
        )}
      />
      <div
        className={clsx(
          'relative -ml-3 flex w-6 shrink-0 flex-col items-center md:order-2 md:ml-0 md:w-12',
          isFirst ? 'pt-5 md:pt-8' : 'pt-11 md:pt-14'
        )}
      >
        <div
          className={clsx('absolute inset-y-0 left-1/2 w-1 -translate-x-1/2', {
            'bg-linear-to-b from-transparent to-gray-300 to-16%': isFirst,
            'bg-linear-to-b from-gray-300 from-39% to-transparent to-65%':
              isLast,
            'bg-gray-300': !isFirst && !isLast,
          })}
        />
        <div className="relative flex h-6 items-center md:h-7">
          <div className="size-5 rounded-full border-4 border-white bg-blue-700 md:size-6" />
        </div>
      </div>
      <div
        className={clsx(
          'flex-1 py-6',
          alignEnd ? 'md:order-3' : 'md:order-1',
          isFirst && 'pt-0',
          isLast && 'pb-0'
        )}
      >
        <StepCard step={step} />
      </div>
    </div>
  )
}

function StepCard({ step }: { step: Step }) {
  return (
    <div className="flex flex-col gap-6 rounded-xl border border-gray-100 bg-linear-to-b from-gray-50 to-gray-100 p-5 md:px-10 md:pt-8 md:pb-10">
      <div className="flex flex-col gap-4">
        <p className="font-bold md:text-lg">{step.step}</p>
        <div className="flex flex-col gap-3 md:gap-4">
          <h3 className="text-2xl font-black md:text-7xl">{step.title}</h3>
          <div className="flex flex-col gap-4 text-gray-800">
            {step.paragraphs.map((paragraph, index) =>
              typeof paragraph === 'string' ? (
                <p key={index}>{paragraph}</p>
              ) : (
                <ul key={index} className="list-disc pl-5">
                  {paragraph.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )
            )}
          </div>
        </div>
      </div>
      {step.note && (
        <div className="flex gap-2">
          <InfoIcon
            size={20}
            weight="bold"
            className="shrink-0 text-gray-800"
          />
          <p className="text-sm text-gray-800">{step.note}</p>
        </div>
      )}
      {step.action && (
        <div>
          <Button as={Link} href={step.action.href} version="gray-outlined">
            {step.action.label}
          </Button>
        </div>
      )}
    </div>
  )
}
